import React, { useState, useEffect } from 'react';
import { FiCheck, FiX, FiClock, FiAlertCircle, FiEye, FiSearch, FiFileText, FiRefreshCw } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const VendorOwnerMenuManagement = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [pendingCreations, setPendingCreations] = useState([]);
  const [pendingUpdates, setPendingUpdates] = useState([]);
  const [pendingDeletes, setPendingDeletes] = useState([]);
  const [approvedItems, setApprovedItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [loadingAction, setLoadingAction] = useState(false);
  const [stats, setStats] = useState({
    pendingCreations: 0,
    pendingUpdates: 0,
    pendingDeletes: 0,
    totalApproved: 0
  });

  // Fetch data on component mount and tab change
  useEffect(() => {
    fetchAllData();
  }, [activeTab]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchPendingCreations(),
        fetchPendingUpdates(),
        fetchPendingDeletes(),
        fetchAllItems(),
        fetchStats()
      ]);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllItems = async () => {
    try {
      const response = await api.get('/vendor-owner/menu-items/all');
      if (response.data.success) {
        const data = response.data.data || [];
        setAllItems(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch all menu items:', error);
      setAllItems([]);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/vendor-owner/dashboard/pending-approvals');
      if (response.data.success) {
        const data = response.data.data || {};
        setStats({
          pendingCreations: data.pendingCreations.count,
          pendingUpdates: data.pendingUpdates.count,
          pendingDeletes: data.pendingDeletes.count,
          totalApproved: typeof data.totalApproved === 'number' ? data.totalApproved : 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      setStats({
        pendingCreations: 0,
        pendingUpdates: 0,
        pendingDeletes: 0,
        totalApproved: 0
      });
    }
  };

  const fetchPendingCreations = async () => {
    try {
      const response = await api.get('/vendor-owner/menu-items/pending-creation');
      if (response.data.success) {
        const data = response.data.data || [];
        setPendingCreations(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch pending creations:', error);
      setPendingCreations([]);
    }
  };

  const fetchPendingUpdates = async () => {
    try {
      const response = await api.get('/vendor-owner/menu-items/pending-updates');
      if (response.data.success) {
        const data = response.data.data || [];
        setPendingUpdates(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch pending updates:', error);
      setPendingUpdates([]);
    }
  };

  const fetchPendingDeletes = async () => {
    try {
      const response = await api.get('/vendor-owner/menu-items/pending-deletes');
      if (response.data.success) {
        const data = response.data.data || [];
        setPendingDeletes(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Failed to fetch pending deletes:', error);
      setPendingDeletes([]);
    }
  };

  const handleApprove = async (type, itemId) => {
    setLoadingAction(true);
    try {
      let endpoint = '';
      switch (type) {
        case 'creation':
          endpoint = `/vendor-owner/menu-items/${itemId}/approve-creation`;
          break;
        case 'update':
          endpoint = `/vendor-owner/menu-items/${itemId}/approve-update`;
          break;
        case 'delete':
          endpoint = `/vendor-owner/menu-items/${itemId}/approve-delete`;
          break;
      }

      const response = await api.post(endpoint);
      if (response.data.success) {
        toast.success('Menu item approved successfully!');
        fetchAllData();
        setShowDetailsModal(false);
      }
    } catch (error) {
      toast.error('Failed to approve menu item');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleReject = async (type, itemId, reason) => {
    if (!reason || reason.trim() === '') {
      toast.error('Please provide a rejection reason');
      return;
    }

    setLoadingAction(true);
    try {
      let endpoint = '';
      switch (type) {
        case 'creation':
          endpoint = `/vendor-owner/menu-items/${itemId}/reject-creation`;
          break;
        case 'update':
          endpoint = `/vendor-owner/menu-items/${itemId}/reject-update`;
          break;
        case 'delete':
          endpoint = `/vendor-owner/menu-items/${itemId}/reject-delete`;
          break;
      }

      const response = await api.post(endpoint, { rejectionReason: reason });
      if (response.data.success) {
        toast.success('Menu item rejected successfully!');
        fetchAllData();
        setShowDetailsModal(false);
      }
    } catch (error) {
      toast.error('Failed to reject menu item');
    } finally {
      setLoadingAction(false);
    }
  };

  const handleApproveAll = async (type) => {
    setLoadingAction(true);
    try {
      let itemsToApprove = [];
      if (type === 'creation') {
        itemsToApprove = pendingCreations;
      } else if (type === 'update') {
        itemsToApprove = pendingUpdates;
      } else if (type === 'delete') {
        itemsToApprove = pendingDeletes;
      }

      if (itemsToApprove.length === 0) {
        toast.info('No items to approve');
        return;
      }

      const itemIds = itemsToApprove.map(item => item._id);
      let endpoint = '';

      switch (type) {
        case 'creation':
          endpoint = '/vendor-owner/menu-items/approve-all-creations';
          break;
        case 'update':
          endpoint = '/vendor-owner/menu-items/approve-all-updates';
          break;
        case 'delete':
          endpoint = '/vendor-owner/menu-items/approve-all-deletes';
          break;
      }

      const response = await api.post(endpoint, { itemIds });
      if (response.data.success) {
        toast.success(`Approved ${itemsToApprove.length} items successfully!`);
        fetchAllData();
      }
    } catch (error) {
      toast.error('Failed to approve items');
    } finally {
      setLoadingAction(false);
    }
  };

  const showItemDetails = (item, type) => {
    setSelectedItem({ ...item, approvalType: type });
    setShowDetailsModal(true);
  };

  const getFilteredItems = (items) => {
    if (!searchTerm) return items;
    return items.filter(item =>
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.categoryId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.subCategoryId?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const renderAllItems = () => {
    const filteredItems = getFilteredItems(allItems);
    return (
      <div className="space-y-4">
        {filteredItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FiFileText className="mx-auto text-4xl mb-2" />
            <p>No menu items found</p>
          </div>
        ) : (
          filteredItems.map((item) => {
            const getStatusBadge = () => {
              if (item.hasPendingDelete) {
                return (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    <FiAlertCircle className="mr-1" size={10} />
                    Pending Delete
                  </span>
                );
              }
              if (item.hasPendingUpdate) {
                return (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    <FiClock className="mr-1" size={10} />
                    Pending Update
                  </span>
                );
              }
              if (!item.isApproved) {
                return (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                    <FiClock className="mr-1" size={10} />
                    Pending Approval
                  </span>
                );
              }
              return (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <FiCheck className="mr-1" size={10} />
                  Approved
                </span>
              );
            };

            return (
              <div key={item._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-start gap-4">
                      <img
                        src={item.images || '/placeholder-food.jpg'}
                        alt={item.name}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900 text-lg">{item.name}</h3>
                          {getStatusBadge()}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{item.description}</p>

                        {/* Vendor & Restaurant Details */}
                        <div className="text-xs text-gray-500 mb-2">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">Vendor:</span>
                            <span>{item.vendorId?.personalDetails?.name || 'Unknown'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Restaurant:</span>
                            <span>{item.vendorId?.restaurantDetails?.restaurantName || 'Unknown'}</span>
                          </div>
                        </div>

                        {/* Item Details */}
                        <div className="flex flex-wrap items-center gap-3 text-sm">
                          <span className="text-gray-500">
                            <span className="font-medium">Category:</span> {item.categoryId?.name || 'N/A'}
                          </span>
                          {item.subCategoryId?.name && (
                            <span className="text-gray-500">
                              <span className="font-medium">SubCategory:</span> {item.subCategoryId?.name}
                            </span>
                          )}
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              {item.discountPrice && item.discountPrice < item.price ? (
                                <>
                                  <span className="font-medium text-gray-900 line-through text-gray-400">₹{item.price}</span>
                                  <span className="font-medium text-green-600">₹{item.discountPrice}</span>
                                </>
                              ) : (
                                <span className="font-medium text-gray-900">₹{item.price}</span>
                              )}
                            </div>
                            {item.pricingUnit && (
                              <span className="text-xs text-gray-500">{item.pricingUnit}</span>
                            )}
                          </div>
                          {item.discountPrice && item.discountPrice < item.price && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              {Math.round(((item.price - item.discountPrice) / item.price) * 100)}% OFF
                            </span>
                          )}
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.isVeg
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                            }`}>
                            {item.isVeg ? 'Veg' : 'Non-Veg'}
                          </span>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.isAvailable
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                            }`}>
                            {item.isAvailable ? 'Available' : 'Unavailable'}
                          </span>
                        </div>

                        {/* Status Details */}
                        <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <span className="font-medium">Status:</span>
                              <span className={`ml-1 ${!item.isApproved ? 'text-yellow-600' :
                                item.hasPendingUpdate ? 'text-orange-600' :
                                  item.hasPendingDelete ? 'text-red-600' : 'text-green-600'
                                }`}>
                                {!item.isApproved ? 'Pending Approval' :
                                  item.hasPendingUpdate ? 'Pending Update' :
                                    item.hasPendingDelete ? 'Pending Delete' : 'Approved'}
                              </span>
                            </div>
                            <div>
                              <span className="font-medium">Created:</span>
                              <span className="ml-1">{new Date(item.createdAt).toLocaleDateString()}</span>
                            </div>
                            {item.approvedAt && (
                              <div>
                                <span className="font-medium">Approved:</span>
                                <span className="ml-1">{new Date(item.approvedAt).toLocaleDateString()}</span>
                              </div>
                            )}
                            {item.hasPendingUpdate && item.pendingUpdateRequestedAt && (
                              <div>
                                <span className="font-medium">Update Req:</span>
                                <span className="ml-1">{new Date(item.pendingUpdateRequestedAt).toLocaleDateString()}</span>
                              </div>
                            )}
                            {item.hasPendingDelete && item.pendingDeleteRequestedAt && (
                              <div>
                                <span className="font-medium">Delete Req:</span>
                                <span className="ml-1">{new Date(item.pendingDeleteRequestedAt).toLocaleDateString()}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2 ml-4">
                    {!item.isApproved && !item.hasPendingDelete && (
                      <button
                        onClick={() => handleApprove('creation', item._id)}
                        disabled={loadingAction}
                        className="px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                      >
                        <FiCheck className="inline mr-1" /> Approve
                      </button>
                    )}
                    {item.hasPendingUpdate && (
                      <button
                        onClick={() => showItemDetails(item, 'update')}
                        className="px-3 py-2 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700 transition-colors whitespace-nowrap"
                      >
                        <FiRefreshCw className="inline mr-1" /> Review
                      </button>
                    )}
                    {item.hasPendingDelete && (
                      <button
                        onClick={() => showItemDetails(item, 'delete')}
                        className="px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors whitespace-nowrap"
                      >
                        <FiAlertCircle className="inline mr-1" /> Review
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  };

  const renderPendingCreations = () => {
    const filteredItems = getFilteredItems(pendingCreations);
    return (
      <div className="space-y-4">
        {filteredItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FiClock className="mx-auto text-4xl mb-2" />
            <p>No pending creations found</p>
          </div>
        ) : (
          <>
            {/* Approve All Button */}
            <div className="flex justify-end mb-4">
              <button
                onClick={() => handleApproveAll('creation')}
                disabled={loadingAction || filteredItems.length === 0}
                className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 whitespace-nowrap flex items-center gap-2"
              >
                <FiCheck className="inline" /> Approve All ({filteredItems.length})
              </button>
            </div>

            {/* Individual Items */}
            {filteredItems.map((item) => (
              <div key={item._id} className="bg-white rounded-lg shadow-sm border border-yellow-200 p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-start gap-4">
                      <img
                        src={item.images || '/placeholder-food.jpg'}
                        alt={item.name}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900 text-lg">{item.name}</h3>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <FiClock className="mr-1" size={10} />
                            Pending Approval
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{item.description}</p>

                        {/* Vendor & Restaurant Details */}
                        <div className="text-xs text-gray-500 mb-2">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">Vendor:</span>
                            <span>{item.vendorId?.personalDetails?.name || 'Unknown'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Restaurant:</span>
                            <span>{item.vendorId?.restaurantDetails?.restaurantName || 'Unknown'}</span>
                          </div>
                        </div>

                        {/* Item Details */}
                        <div className="flex flex-wrap items-center gap-3 text-sm">
                          <span className="text-gray-500">
                            <span className="font-medium">Category:</span> {item.categoryId?.name || 'N/A'}
                          </span>
                          {item.subCategoryId?.name && (
                            <span className="text-gray-500">
                              <span className="font-medium">SubCategory:</span> {item.subCategoryId?.name}
                            </span>
                          )}
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              {item.discountPrice && item.discountPrice < item.price ? (
                                <>
                                  <span className="font-medium text-gray-900 line-through text-gray-400">₹{item.price}</span>
                                  <span className="font-medium text-green-600">₹{item.discountPrice}</span>
                                </>
                              ) : (
                                <span className="font-medium text-gray-900">₹{item.price}</span>
                              )}
                            </div>
                            {item.pricingUnit && (
                              <span className="text-xs text-gray-500">{item.pricingUnit}</span>
                            )}
                          </div>
                          {item.discountPrice && item.discountPrice < item.price && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              {Math.round(((item.price - item.discountPrice) / item.price) * 100)}% OFF
                            </span>
                          )}
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.isVeg
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                            }`}>
                            {item.isVeg ? 'Veg' : 'Non-Veg'}
                          </span>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.isAvailable
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                            }`}>
                            {item.isAvailable ? 'Available' : 'Unavailable'}
                          </span>
                        </div>

                        {/* Status Details */}
                        <div className="mt-2 p-2 bg-yellow-50 rounded text-xs">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <span className="font-medium">Status:</span>
                              <span className="ml-1 text-yellow-600">Pending Approval</span>
                            </div>
                            <div>
                              <span className="font-medium">Created:</span>
                              <span className="ml-1">{new Date(item.createdAt).toLocaleDateString()}</span>
                            </div>
                            {item.pendingUpdateRequestedAt && (
                              <div>
                                <span className="font-medium">Requested:</span>
                                <span className="ml-1">{new Date(item.pendingUpdateRequestedAt).toLocaleDateString()}</span>
                              </div>
                            )}
                            <div>
                              <span className="font-medium">Requested By:</span>
                              <span className="ml-1">{item.pendingUpdateRequestedBy?.name || item.pendingDeleteRequestedBy?.name || 'Vendor'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2 ml-4">
                    <button
                      onClick={() => handleApprove('creation', item._id)}
                      disabled={loadingAction}
                      className="px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                      <FiCheck className="inline mr-1" /> Approve
                    </button>
                    <button
                      onClick={() => {
                        const reason = prompt('Please enter rejection reason:');
                        if (reason) {
                          handleReject('creation', item._id, reason);
                        }
                      }}
                      disabled={loadingAction}
                      className="px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                      <FiX className="inline mr-1" /> Reject
                    </button>
                  </div>
                </div>
              </div>
            ))
            }
          </>
        )}
      </div>
    );
  };

  const renderPendingUpdates = () => {
    const filteredItems = getFilteredItems(pendingUpdates);
    return (
      <div className="space-y-4">
        {filteredItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FiRefreshCw className="mx-auto text-4xl mb-2" />
            <p>No pending updates found</p>
          </div>
        ) : (
          <>
            {/* Approve All Button */}
            <div className="flex justify-end mb-4">
              <button
                onClick={() => handleApproveAll('update')}
                disabled={loadingAction || filteredItems.length === 0}
                className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 whitespace-nowrap flex items-center gap-2"
              >
                <FiCheck className="inline" /> Approve All ({filteredItems.length})
              </button>
            </div>

            {/* Individual Items */}
            {filteredItems.map((item) => (
              <div key={item._id} className="bg-white rounded-lg shadow-sm border border-orange-200 p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-start gap-4">
                      <img
                        src={item.images || '/placeholder-food.jpg'}
                        alt={item.name}
                        className="w-20 h-20 object-cover rounded-lg"
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-gray-900 text-lg">{item.name}</h3>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            <FiClock className="mr-1" size={10} />
                            Pending Update
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{item.description}</p>

                        {/* Pending Changes Preview */}
                        {item.pendingUpdateData && (
                          <div className="mb-2 p-2 bg-orange-50 rounded text-xs">
                            <p className="font-medium text-orange-800 mb-1">Pending Changes:</p>
                            <div className="space-y-1">
                              {item.pendingUpdateData.name !== item.name && (
                                <div><span className="text-gray-600">Name:</span> {item.name} → {item.pendingUpdateData.name}</div>
                              )}
                              {(item.pendingUpdateData?.itemCode || '') !== (item.itemCode || '') && (
                                <div>
                                  <span className="text-gray-600">Item Code: </span>
                                  {item.itemCode || 'N/A'} → {item.pendingUpdateData?.itemCode || 'N/A'}
                                </div>
                              )}
                              {item.pendingUpdateData.price !== item.price && (
                                <div><span className="text-gray-600">Price:</span> ₹{item.price} → ₹{item.pendingUpdateData.price}</div>
                              )}
                              {item.pendingUpdateData.discountPrice !== item.discountPrice && (
                                <div><span className="text-gray-600">Discount Price:</span>
                                  {item.discountPrice ? `₹${item.discountPrice}` : 'No Discount'} → {item.pendingUpdateData.discountPrice ? `₹${item.pendingUpdateData.discountPrice}` : 'No Discount'}
                                </div>
                              )}
                              {item.pendingUpdateData.isVeg !== undefined && item.pendingUpdateData.isVeg !== item.isVeg && (
                                <div><span className="text-gray-600">Type:</span> {item.isVeg ? 'Veg' : 'Non-Veg'} → {item.pendingUpdateData.isVeg ? 'Veg' : 'Non-Veg'}</div>
                              )}
                              {item.pendingUpdateData?.preparationTime !== undefined && item.pendingUpdateData.preparationTime !== item.preparationTime && (
                                <div><span className="text-gray-600">Preparation Time:</span>
                                  {item.preparationTime} min → {item.pendingUpdateData.preparationTime} min
                                </div>
                              )}
                              {item.pendingUpdateData.isAvailable !== undefined && item.pendingUpdateData.isAvailable !== item.isAvailable && (
                                <div><span className="text-gray-600">Availability:</span> {item.isAvailable ? 'Available' : 'Unavailable'} → {item.pendingUpdateData.isAvailable ? 'Available' : 'Unavailable'}</div>
                              )}
                              {item.pendingUpdateData.description !== item.description && (
                                <div><span className="text-gray-600">Description:</span> Updated</div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Vendor & Restaurant Details */}
                        <div className="text-xs text-gray-500 mb-2">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-medium">Vendor:</span>
                            <span>{item.vendorId?.personalDetails?.name || 'Unknown'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">Restaurant:</span>
                            <span>{item.vendorId?.restaurantDetails?.restaurantName || 'Unknown'}</span>
                          </div>
                        </div>

                        {/* Item Details */}
                        <div className="flex flex-wrap items-center gap-3 text-sm">
                          <span className="text-gray-500">
                            <span className="font-medium">Category:</span> {item.categoryId?.name || 'N/A'}
                          </span>
                          {item.subCategoryId?.name && (
                            <span className="text-gray-500">
                              <span className="font-medium">SubCategory:</span> {item.subCategoryId?.name}
                            </span>
                          )}
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-2">
                              {item.discountPrice && item.discountPrice < item.price ? (
                                <>
                                  <span className="font-medium text-gray-900 line-through text-gray-400">₹{item.price}</span>
                                  <span className="font-medium text-green-600">₹{item.discountPrice}</span>
                                </>
                              ) : (
                                <span className="font-medium text-gray-900">₹{item.price}</span>
                              )}
                            </div>
                            {item.pricingUnit && (
                              <span className="text-xs text-gray-500">{item.pricingUnit}</span>
                            )}
                          </div>
                          {item.discountPrice && item.discountPrice < item.price && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                              {Math.round(((item.price - item.discountPrice) / item.price) * 100)}% OFF
                            </span>
                          )}
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.isVeg
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                            }`}>
                            {item.isVeg ? 'Veg' : 'Non-Veg'}
                          </span>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.isAvailable
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                            }`}>
                            {item.isAvailable ? 'Available' : 'Unavailable'}
                          </span>
                        </div>

                        {/* Status Details */}
                        <div className="mt-2 p-2 bg-orange-50 rounded text-xs">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <span className="font-medium">Status:</span>
                              <span className="ml-1 text-orange-600">Pending Update</span>
                            </div>
                            <div>
                              <span className="font-medium">Created:</span>
                              <span className="ml-1">{new Date(item.createdAt).toLocaleDateString()}</span>
                            </div>
                            {item.pendingUpdateRequestedAt && (
                              <div>
                                <span className="font-medium">Update Req:</span>
                                <span className="ml-1">{new Date(item.pendingUpdateRequestedAt).toLocaleDateString()}</span>
                              </div>
                            )}
                            <div>
                              <span className="font-medium">Requested By:</span>
                              <span className="ml-1">{item.pendingUpdateRequestedBy?.name || 'Vendor'}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col gap-2 ml-4">
                    <button
                      onClick={() => handleApprove('update', item._id)}
                      disabled={loadingAction}
                      className="px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                      <FiCheck className="inline mr-1" /> Approve
                    </button>
                    <button
                      onClick={() => {
                        const reason = prompt('Please enter rejection reason:');
                        if (reason) {
                          handleReject('update', item._id, reason);
                        }
                      }}
                      disabled={loadingAction}
                      className="px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                      <FiX className="inline mr-1" /> Reject
                    </button>
                  </div>
                </div>
              </div>
            ))
            }
          </>
        )}
      </div>
    );
  };

  const renderPendingDeletes = () => {
    const filteredItems = getFilteredItems(pendingDeletes);
    return (
      <div className="space-y-4">
        {filteredItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FiAlertCircle className="mx-auto text-4xl mb-2" />
            <p>No pending deletions found</p>
          </div>
        ) : (
          filteredItems.map((item) => (
            <div key={item._id} className="bg-white rounded-lg shadow-sm border border-red-200 p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-start gap-4">
                    <img
                      src={item.images || '/placeholder-food.jpg'}
                      alt={item.name}
                      className="w-20 h-20 object-cover rounded-lg opacity-50"
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-900 text-lg line-through">{item.name}</h3>
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          <FiAlertCircle className="mr-1" size={10} />
                          Pending Delete
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{item.description}</p>

                      {/* Vendor & Restaurant Details */}
                      <div className="text-xs text-gray-500 mb-2">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">Vendor:</span>
                          <span>{item.vendorId?.personalDetails?.name || 'Unknown'}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">Restaurant:</span>
                          <span>{item.vendorId?.restaurantDetails?.restaurantName || 'Unknown'}</span>
                        </div>
                      </div>

                      {/* Item Details */}
                      <div className="flex flex-wrap items-center gap-3 text-sm">
                        <span className="text-gray-500">
                          <span className="font-medium">Category:</span> {item.categoryId?.name || 'N/A'}
                        </span>
                        {item.subCategoryId?.name && (
                          <span className="text-gray-500">
                            <span className="font-medium">SubCategory:</span> {item.subCategoryId?.name}
                          </span>
                        )}
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            {item.discountPrice && item.discountPrice < item.price ? (
                              <>
                                <span className="font-medium text-gray-900 line-through text-gray-400">₹{item.price}</span>
                                <span className="font-medium text-green-600">₹{item.discountPrice}</span>
                              </>
                            ) : (
                              <span className="font-medium text-gray-900">₹{item.price}</span>
                            )}
                          </div>
                          {item.pricingUnit && (
                            <span className="text-xs text-gray-500">{item.pricingUnit}</span>
                          )}
                        </div>
                        {item.discountPrice && item.discountPrice < item.price && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            {Math.round(((item.price - item.discountPrice) / item.price) * 100)}% OFF
                          </span>
                        )}
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.isVeg
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                          }`}>
                          {item.isVeg ? 'Veg' : 'Non-Veg'}
                        </span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${item.isAvailable
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                          }`}>
                          {item.isAvailable ? 'Available' : 'Unavailable'}
                        </span>
                      </div>

                      {/* Status Details */}
                      <div className="mt-2 p-2 bg-red-50 rounded text-xs">
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <span className="font-medium">Status:</span>
                            <span className="ml-1 text-red-600">Pending Delete</span>
                          </div>
                          <div>
                            <span className="font-medium">Created:</span>
                            <span className="ml-1">{new Date(item.createdAt).toLocaleDateString()}</span>
                          </div>
                          {item.pendingDeleteRequestedAt && (
                            <div>
                              <span className="font-medium">Delete Req:</span>
                              <span className="ml-1">{new Date(item.pendingDeleteRequestedAt).toLocaleDateString()}</span>
                            </div>
                          )}
                          <div>
                            <span className="font-medium">Requested By:</span>
                            <span className="ml-1">{item.pendingDeleteRequestedBy?.name || 'Vendor'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-2 ml-4">
                  <button
                    onClick={() => handleApprove('delete', item._id)}
                    disabled={loadingAction}
                    className="px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                  >
                    <FiCheck className="inline mr-1" /> Approve
                  </button>
                  <button
                    onClick={() => {
                      const reason = prompt('Please enter rejection reason:');
                      if (reason) {
                        handleReject('delete', item._id, reason);
                      }
                    }}
                    disabled={loadingAction}
                    className="px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                  >
                    <FiX className="inline mr-1" /> Reject
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    );
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Menu Management</h1>
        <p className="text-gray-600 mt-1">Review and approve menu item changes from vendors</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Creations</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.pendingCreations}</p>
            </div>
            <FiClock className="text-yellow-600 text-2xl" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Updates</p>
              <p className="text-2xl font-bold text-orange-600">{stats.pendingUpdates}</p>
            </div>
            <FiRefreshCw className="text-orange-600 text-2xl" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending Deletes</p>
              <p className="text-2xl font-bold text-red-600">{stats.pendingDeletes}</p>
            </div>
            <FiAlertCircle className="text-red-600 text-2xl" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm p-4 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Approved</p>
              <p className="text-2xl font-bold text-green-600">{stats.totalApproved}</p>
            </div>
            <FiCheck className="text-green-600 text-2xl" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'all'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              onClick={() => setActiveTab('all')}
            >
              All Items
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                {allItems.length}
              </span>
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'pending-creations'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              onClick={() => setActiveTab('pending-creations')}
            >
              Pending Creations
              {stats.pendingCreations > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  {stats.pendingCreations}
                </span>
              )}
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'pending-updates'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              onClick={() => setActiveTab('pending-updates')}
            >
              Pending Updates
              {stats.pendingUpdates > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  {stats.pendingUpdates}
                </span>
              )}
            </button>
            <button
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${activeTab === 'pending-deletes'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              onClick={() => setActiveTab('pending-deletes')}
            >
              Pending Deletes
              {stats.pendingDeletes > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  {stats.pendingDeletes}
                </span>
              )}
            </button>
          </nav>
        </div>

        {/* Search Bar */}
        <div className="p-4 border-b border-gray-200">
          <div className="relative max-w-md">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              placeholder="Search menu items..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <>
              {activeTab === 'all' && renderAllItems()}
              {activeTab === 'pending-creations' && renderPendingCreations()}
              {activeTab === 'pending-updates' && renderPendingUpdates()}
              {activeTab === 'pending-deletes' && renderPendingDeletes()}
            </>
          )}
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Menu Item Details</h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FiX size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <img
                    src={selectedItem.images || '/placeholder-food.jpg'}
                    alt={selectedItem.name}
                    className="w-24 h-24 object-cover rounded-lg"
                  />
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">{selectedItem.name}</h4>
                    <p className="text-gray-600">{selectedItem.description}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-sm text-gray-500">{selectedItem.categoryId?.name}</span>
                      {selectedItem.subCategoryId?.name && (
                        <span className="text-sm text-gray-500">• {selectedItem.subCategoryId?.name}</span>
                      )}
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          {selectedItem.discountPrice && selectedItem.discountPrice < selectedItem.price ? (
                            <>
                              <span className="text-sm font-medium text-gray-900 line-through text-gray-400">₹{selectedItem.price}</span>
                              <span className="text-sm font-medium text-green-600">₹{selectedItem.discountPrice}</span>
                            </>
                          ) : (
                            <span className="text-sm font-medium text-gray-900">₹{selectedItem.price}</span>
                          )}
                        </div>
                        {selectedItem.pricingUnit && (
                          <span className="text-xs text-gray-500">{selectedItem.pricingUnit}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {selectedItem.approvalType === 'update' && selectedItem.pendingUpdateData && (
                  <div className="p-4 bg-orange-50 rounded-lg">
                    <h5 className="font-medium text-orange-800 mb-2">Pending Changes:</h5>
                    <div className="space-y-2 text-sm">
                      {selectedItem.pendingUpdateData.name !== selectedItem.name && (
                        <div><span className="text-gray-600">Name:</span> {selectedItem.name} → {selectedItem.pendingUpdateData.name}</div>
                      )}
                      {(selectedItem.pendingUpdateData?.itemCode || '') !== (selectedItem.itemCode || '') && (
                        <div>
                          <span className="text-gray-600">Item Code: </span>
                          {selectedItem.itemCode || 'N/A'} → {selectedItem.pendingUpdateData?.itemCode || 'N/A'}
                        </div>
                      )}
                      {selectedItem.pendingUpdateData.price !== selectedItem.price && (
                        <div><span className="text-gray-600">Original Price:</span> ₹{selectedItem.price} → ₹{selectedItem.pendingUpdateData.price}</div>
                      )}
                      {selectedItem.pendingUpdateData.discountPrice !== selectedItem.discountPrice && (
                        <div><span className="text-gray-600">Discount Price:</span>
                          {selectedItem.discountPrice ? (
                            <>₹{selectedItem.discountPrice} → {selectedItem.pendingUpdateData.discountPrice ? `₹${selectedItem.pendingUpdateData.discountPrice}` : 'No Discount'}</>
                          ) : (
                            <>No Discount → {selectedItem.pendingUpdateData.discountPrice ? `₹${selectedItem.pendingUpdateData.discountPrice}` : 'No Discount'}</>
                          )}
                        </div>
                      )}
                      {selectedItem.pendingUpdateData.isVeg !== undefined && selectedItem.pendingUpdateData.isVeg !== selectedItem.isVeg && (
                        <div><span className="text-gray-600">Type:</span>
                          {selectedItem.isVeg ? 'Veg' : 'Non-Veg'} → {selectedItem.pendingUpdateData.isVeg ? 'Veg' : 'Non-Veg'}
                        </div>
                      )}
                      {selectedItem.pendingUpdateData?.preparationTime !== undefined && selectedItem.pendingUpdateData.preparationTime !== selectedItem.preparationTime && (
                        <div><span className="text-gray-600">Preparation Time:</span>
                          {selectedItem.preparationTime} min → {selectedItem.pendingUpdateData.preparationTime} min
                        </div>
                      )}
                      {selectedItem.pendingUpdateData.isAvailable !== undefined && selectedItem.pendingUpdateData.isAvailable !== selectedItem.isAvailable && (
                        <div><span className="text-gray-600">Availability:</span>
                          {selectedItem.isAvailable ? 'Available' : 'Unavailable'} → {selectedItem.pendingUpdateData.isAvailable ? 'Available' : 'Unavailable'}
                        </div>
                      )}
                      {/* Show selling price comparison if prices are changing */}
                      {(selectedItem.pendingUpdateData.price !== selectedItem.price || selectedItem.pendingUpdateData.discountPrice !== selectedItem.discountPrice) && (
                        <div className="pt-2 mt-2 border-t border-orange-200">
                          <span className="text-gray-600 font-medium">Selling Price:</span>
                          <span className="ml-2 text-orange-700 font-medium">
                            {(() => {
                              const currentSellingPrice = selectedItem.discountPrice && selectedItem.discountPrice < selectedItem.price
                                ? selectedItem.discountPrice
                                : selectedItem.price;
                              const newSellingPrice = selectedItem.pendingUpdateData.discountPrice && selectedItem.pendingUpdateData.discountPrice < selectedItem.pendingUpdateData.price
                                ? selectedItem.pendingUpdateData.discountPrice
                                : selectedItem.pendingUpdateData.price;
                              return `₹${currentSellingPrice} → ₹${newSellingPrice}`;
                            })()}
                          </span>
                        </div>
                      )}
                      {selectedItem.pendingUpdateData.description !== selectedItem.description && (
                        <div><span className="text-gray-600">Description:</span> Updated</div>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Requested by:</p>
                    <p className="font-medium">{selectedItem.pendingUpdateRequestedBy?.name || selectedItem.pendingDeleteRequestedBy?.name || 'Vendor'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Requested at:</p>
                    <p className="font-medium">
                      {new Date(
                        selectedItem.pendingUpdateRequestedAt ||
                        selectedItem.pendingDeleteRequestedAt ||
                        selectedItem.createdAt
                      ).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => {
                    const reason = prompt('Please enter rejection reason:');
                    if (reason) {
                      handleReject(selectedItem.approvalType, selectedItem._id, reason);
                    }
                  }}
                  disabled={loadingAction}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  <FiX className="inline mr-2" /> Reject
                </button>
                <button
                  onClick={() => handleApprove(selectedItem.approvalType, selectedItem._id)}
                  disabled={loadingAction}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  <FiCheck className="inline mr-2" /> Approve
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorOwnerMenuManagement;
